import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Loader2,
  FileText,
  Boxes,
  Wallet,
  Landmark,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const FEATURES = [
  {
    icon: FileText,
    title: "Rechnungen erstellen",
    desc: "Schnell, rechtssicher & §25a Differenzbesteuerung",
  },
  {
    icon: Boxes,
    title: "Waren verwalten",
    desc: "Ankauf, Verkauf, Seriennummern & IMEI im Griff",
  },
  {
    icon: Wallet,
    title: "Finanzen im Blick",
    desc: "Echtzeit-Auswertung für Umsatz, Gewinn & Margen",
  },
  {
    icon: Landmark,
    title: "Für Steuerberater & Unternehmen",
    desc: "Vorbereitete Exporte & rechtssichere Belege",
  },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);

    try {
      const data = await login(form.email, form.password);
      const dest =
        data.user.role === "superadmin"
          ? "/admin"
          : loc.state?.from || "/app";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(
        apiError(
          err,
          "E-Mail-Adresse oder Passwort ist nicht korrekt."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const forgot = async (e) => {
    e?.preventDefault();
    if (!forgotEmail) {
      toast.error("Bitte gib deine E-Mail-Adresse ein.");
      return;
    }
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(
        "Falls ein Konto existiert, wurde eine E-Mail zum Zurücksetzen gesendet."
      );
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(apiError(err, "Anfrage konnte nicht verarbeitet werden."));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-[#FAFAFA] flex text-zinc-900 select-none md:select-text"
      data-testid="login-page"
    >
      {/* DESKTOP LEFT PRODUCT PANEL */}
      <div className="hidden lg:flex lg:w-1/2 bg-zinc-900 text-white flex-col justify-between p-12 relative overflow-hidden">
        {/* Subtle decorative grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#27272a15_1px,transparent_1px),linear-gradient(to_bottom,#27272a15_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-zinc-800/40 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-3 transition-opacity hover:opacity-90"
            data-testid="login-brand-logo-desktop"
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-zinc-900 text-xs font-bold font-mono tracking-wider">
                L19
              </span>
            </div>
            <div>
              <span className="font-display font-bold text-xl tracking-tight text-white">
                L<span className="text-zinc-400">19</span>
              </span>
              <span className="ml-2 text-[10px] font-mono tracking-widest uppercase bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded-full border border-zinc-700">
                SaaS
              </span>
            </div>
          </Link>
        </div>

        <div className="relative z-10 max-w-lg my-auto py-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-800/80 border border-zinc-700/60 text-xs font-mono text-zinc-300 mb-6">
            <ShieldCheck className="w-3.5 h-3.5 text-zinc-400" />
            <span>Multi-Tenant Händler-Plattform</span>
          </div>

          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
            Dein Business. <br />
            <span className="text-zinc-400">Immer dabei.</span>
          </h2>

          <p className="mt-4 text-zinc-400 text-base leading-relaxed">
            Die Komplettlösung für modernen Handel: Ankauf, Verkauf, Lager,
            Finanzen und Steuerberater-Export – in einem professionellen System.
          </p>

          <div className="mt-8 space-y-4">
            {FEATURES.map((feat) => {
              const IconComponent = feat.icon;
              return (
                <div
                  key={feat.title}
                  className="flex items-start gap-3.5 p-3 rounded-xl bg-zinc-800/40 border border-zinc-800/80 backdrop-blur-sm transition-all hover:bg-zinc-800/60 hover:border-zinc-700/80"
                >
                  <div className="p-2 rounded-lg bg-zinc-800 text-white shrink-0 mt-0.5 border border-zinc-700/50">
                    <IconComponent className="w-4 h-4 text-zinc-200" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white flex items-center gap-2">
                      {feat.title}
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">
                      {feat.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-between text-xs font-mono text-zinc-500 pt-6 border-t border-zinc-800/80">
          <span>© 2026 L19 Platform</span>
          <div className="flex items-center gap-4">
            <a href="/#faq" className="hover:text-zinc-300 transition-colors">
              Hilfe & Support
            </a>
            <span>·</span>
            <span className="flex items-center gap-1.5 text-zinc-400">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> System Online
            </span>
          </div>
        </div>
      </div>

      {/* RIGHT LOGIN CARD CONTAINER */}
      <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-6 lg:p-12 w-full max-w-md lg:max-w-none mx-auto min-h-screen lg:min-h-0">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="w-full max-w-[420px] bg-white border border-zinc-200/80 rounded-2xl shadow-xl shadow-zinc-950/5 p-6 sm:p-8"
        >
          {/* LOGO HEADER */}
          <div className="flex flex-col items-center text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2.5 group focus:outline-none focus:ring-2 focus:ring-zinc-900 rounded-lg p-1"
              data-testid="login-brand-logo-mobile"
            >
              <div className="w-10 h-10 bg-zinc-900 rounded-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                <span className="text-white text-xs font-bold font-mono tracking-wider">
                  L19
                </span>
              </div>
              <span className="font-display font-bold text-2xl tracking-tight text-zinc-900">
                L<span className="text-zinc-400">19</span>
              </span>
            </Link>

            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 mt-5">
              Willkommen zurück
            </h1>
            <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">
              Melde dich an, um mit L19 weiterzuarbeiten.
            </p>
          </div>

          {/* LOGIN FORM */}
          <form
            onSubmit={submit}
            className="mt-6 space-y-4"
            data-testid="login-form"
            noValidate
          >
            {/* EMAIL FIELD */}
            <div>
              <Label
                htmlFor="email"
                className="text-xs font-semibold uppercase tracking-wider text-zinc-700"
              >
                E-Mail-Adresse
              </Label>
              <div className="relative mt-1.5">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
                  <Mail className="w-4 h-4" />
                </div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder="name@firma.de"
                  data-testid="login-email-input"
                  value={form.email}
                  onChange={(e) =>
                    setForm({ ...form, email: e.target.value })
                  }
                  className="pl-10 h-11 bg-zinc-50/50 border-zinc-200 focus:bg-white focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 text-sm transition-all rounded-lg"
                />
              </div>
            </div>

            {/* PASSWORD FIELD */}
            <div>
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="password"
                  className="text-xs font-semibold uppercase tracking-wider text-zinc-700"
                >
                  Passwort
                </Label>
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="forgot-password-link"
                  className="text-xs font-medium text-zinc-500 hover:text-zinc-900 hover:underline focus:outline-none transition-colors"
                >
                  Passwort vergessen?
                </button>
              </div>
              <div className="relative mt-1.5">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
                  <Lock className="w-4 h-4" />
                </div>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                  className="pl-10 pr-10 h-11 bg-zinc-50/50 border-zinc-200 focus:bg-white focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 text-sm transition-all rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="login-password-toggle"
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-zinc-400 hover:text-zinc-700 focus:outline-none transition-colors"
                  aria-label={
                    showPassword ? "Passwort verbergen" : "Passwort anzeigen"
                  }
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* ERROR DISPLAY WITH DECENT SHAKE */}
            <AnimatePresence mode="wait">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0, x: [0, -4, 4, -4, 4, 0] }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                  className="text-xs font-medium text-red-600 bg-red-50 border border-red-200/80 rounded-lg px-3.5 py-2.5 flex items-center gap-2"
                  data-testid="login-error"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600 shrink-0" />
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* SUBMIT BUTTON */}
            <motion.div whileTap={{ scale: 0.985 }}>
              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full h-11 mt-2 bg-zinc-900 hover:bg-zinc-800 text-white font-medium rounded-lg shadow-sm transition-all flex items-center justify-center gap-2 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                    <span>Anmeldung läuft…</span>
                  </>
                ) : (
                  <>
                    <span>Anmelden</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </motion.div>
          </form>

          {/* REGISTER LINK FOOTER */}
          <div className="mt-6 pt-5 border-t border-zinc-100 text-center text-xs text-zinc-500">
            <span>Noch kein Konto? </span>
            <Link
              to="/registrieren"
              className="text-zinc-900 font-semibold hover:underline focus:outline-none"
              data-testid="login-register-link"
            >
              Kostenlos registrieren
            </Link>
          </div>
        </motion.div>
      </div>

      {/* FORGOT PASSWORD DIALOG */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent
          className="bg-white rounded-2xl sm:max-w-md p-6"
          data-testid="forgot-password-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold text-zinc-900">
              Passwort vergessen?
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 mt-1">
              Gib deine registrierte E-Mail-Adresse ein. Wir senden dir einen
              Link zum Zurücksetzen deines Passworts.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={forgot} className="space-y-4 mt-4">
            <div>
              <Label
                htmlFor="forgot-email"
                className="text-xs font-semibold uppercase tracking-wider text-zinc-700"
              >
                E-Mail-Adresse
              </Label>
              <Input
                id="forgot-email"
                type="email"
                required
                placeholder="name@firma.de"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                data-testid="forgot-email-input"
                className="mt-1.5 h-11 bg-zinc-50 border-zinc-200 focus:bg-white focus:border-zinc-900 text-sm rounded-lg"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setForgotOpen(false)}
                className="h-10 text-xs border-zinc-200 text-zinc-700 hover:bg-zinc-100"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={forgotLoading}
                data-testid="forgot-submit-button"
                className="h-10 text-xs bg-zinc-900 hover:bg-zinc-800 text-white font-medium px-4 rounded-lg"
              >
                {forgotLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  "Link anfordern"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
